import { ActionStatus, ActionTargetResource, ActionType, actionConfig, } from './action';
import { EscalationStatus } from './escalation';
import { PolicyEscalationType, PolicyType } from './policy';
export const ESCALATION_WEBHOOK_EVENTS = [`note_added`, `completed`, `cancelled`, `reopened`];
export const escalationWebhookEventKey = (event) => `escalation.${event}`;
const actionEvent = (key, label, children) => ({
    key,
    label,
    description: `Fires when a ${label.toLowerCase()} action completes or fails (for example, the invoice is in a locked period or the email bounces). Includes the action, its policy and the contact.`,
    children,
});
const escalationEvent = (event, label, description) => ({
    key: escalationWebhookEventKey(event),
    label,
    description,
});
export const WEBHOOK_EVENT_GROUPS = [
    {
        name: `Invoice actions`,
        events: [
            actionEvent(ActionType.REMINDER, `Reminder email`),
            actionEvent(ActionType.LATE_FEE, `Late fee`),
            actionEvent(ActionType.LATE_FEE_EMAIL, `Late fee email`),
            actionEvent(ActionType.SEND_SMS, `SMS`),
            actionEvent(ActionType.DISCOUNT_APPLY, `Discount applied`),
            actionEvent(ActionType.DISCOUNT_REMOVE, `Discount removed`),
            actionEvent(ActionType.DISCOUNT_APPLIED_EMAIL, `Discount applied email`),
            actionEvent(ActionType.DISCOUNT_REMOVED_EMAIL, `Discount removed email`),
            actionEvent(ActionType.ESCALATION, `Escalation`, [
                escalationEvent(`note_added`, `Note added`, `Fires when someone adds a note to an escalation. Includes the escalation, its notes and the contact.`),
                escalationEvent(`completed`, `Completed`, `Fires when an escalation is marked complete. Includes the escalation, its notes and the contact.`),
                escalationEvent(`cancelled`, `Cancelled`, `Fires when an escalation is cancelled. Includes the escalation, its notes and the contact.`),
                escalationEvent(`reopened`, `Reopened`, `Fires when a completed or cancelled escalation is reopened. Includes the escalation, its notes and the contact.`),
            ]),
        ],
    },
    {
        name: `Contact actions`,
        events: [
            actionEvent(ActionType.SEND_STATEMENT, `Statement email`),
            actionEvent(ActionType.ISSUE_STATEMENT_LATE_FEE, `Statement interest`),
            actionEvent(ActionType.SEND_STATEMENT_INTEREST_EMAIL, `Statement interest email`),
            actionEvent(ActionType.SEND_ANNOUNCEMENT, `Announcement email`),
        ],
    },
    {
        name: `Quotes & payment plans`,
        events: [
            actionEvent(ActionType.QUOTE_EMAIL, `Quote email`),
            actionEvent(ActionType.PAYMENT_PLAN_REMINDER, `Payment plan reminder`),
        ],
    },
];
export const WEBHOOK_EVENTS = WEBHOOK_EVENT_GROUPS.flatMap(g => g.events.flatMap(e => [e, ...(e.children || [])]));
export const WEBHOOK_EVENT_KEYS = WEBHOOK_EVENTS.map(e => e.key);
const SAMPLE_DATE = `2026-10-14T10:47:00.000Z`;
const SAMPLE_CONTACT = {
    accountingSystemId: `9b2c4f1e-3a7d-4c8e-b1f0-6d5a2e8c7b34`,
    customerName: `Acme Supplies Ltd`,
    status: `ACTIVE`,
    portalLink: `https://portal.paidnice.com/c/3f9a1c`,
    customerCountry: `NZ`,
    customerCity: `Auckland`,
    primaryContactPerson: {
        isPrimaryContact: true,
        firstName: `Jane`,
        lastName: `Smith`,
        email: `jane@acmesupplies.com`,
        mobileNumber: `211234567`,
        mobileCountryCode: `64`,
    },
    contactPersons: [
        {
            isPrimaryContact: true,
            firstName: `Jane`,
            lastName: `Smith`,
            email: `jane@acmesupplies.com`,
            mobileNumber: `211234567`,
            mobileCountryCode: `64`,
        },
    ],
};
const SAMPLE_INVOICE = {
    accountingSystemInvoiceId: `5e7d1a2b-8c4f-4b6e-9a3d-2f1c0b7e6d58`,
    invoiceReference: `PO-7781`,
    invoiceNumber: `INV-0042`,
    invoiceDueDate: `2026-09-30T00:00:00.000Z`,
    invoiceCurrency: `NZD`,
    invoiceTotal: 1250,
    invoiceBalanceDue: 1250,
    portalLink: `https://portal.paidnice.com/i/8d2e4b`,
    accountingLink: `https://go.xero.com/app/invoicing/view/5e7d1a2b-8c4f-4b6e-9a3d-2f1c0b7e6d58`,
};
// Follow-up actions have no origin policy type in actionConfig, so use the policy that creates them
const SAMPLE_POLICY_TYPE = {
    [ActionType.LATE_FEE_EMAIL]: PolicyType.LATE_FEE,
    [ActionType.DISCOUNT_REMOVE]: PolicyType.DISCOUNT,
    [ActionType.DISCOUNT_APPLIED_EMAIL]: PolicyType.DISCOUNT,
    [ActionType.DISCOUNT_REMOVED_EMAIL]: PolicyType.DISCOUNT,
    [ActionType.SEND_STATEMENT]: PolicyType.STATEMENT,
    [ActionType.ISSUE_STATEMENT_LATE_FEE]: PolicyType.STATEMENT_LATE_FEE,
    [ActionType.SEND_STATEMENT_INTEREST_EMAIL]: PolicyType.STATEMENT_LATE_FEE,
};
/** An example of what we send for an event, built to match the real payload shape in outboundWebhookService */
export function buildSampleWebhookPayload(event, status) {
    if (event.startsWith(`escalation.`)) {
        const escalationEvent = event.replace(`escalation.`, ``);
        return Object.assign(Object.assign(Object.assign({ webhookOnly: false, contact: SAMPLE_CONTACT, policy: { policyId: `1024`, policyTitle: `Escalate to call after 60 days`, policyType: PolicyType.ESCALATION }, invoice: SAMPLE_INVOICE, escalationType: PolicyEscalationType.CALL, escalationId: `512`, escalationStatus: {
                completed: EscalationStatus.COMPLETE,
                cancelled: EscalationStatus.CANCELLED,
                note_added: EscalationStatus.PENDING,
                reopened: EscalationStatus.PENDING,
            }[escalationEvent], escalationDate: SAMPLE_DATE, escalationNotes: [
                { user: `sam@yourcompany.com`, date: SAMPLE_DATE, note: `Called Jane, payment promised for Friday.` },
            ] }, (escalationEvent === `completed` ? { completedDate: SAMPLE_DATE } : {})), (escalationEvent === `cancelled` ? { cancelledDate: SAMPLE_DATE } : {})), { escalationEvent });
    }
    const actionType = event;
    const config = actionConfig[actionType];
    const policyType = config.originPolicyType || SAMPLE_POLICY_TYPE[actionType];
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ webhookOnly: false, contact: SAMPLE_CONTACT }, (policyType ? { policy: { policyId: `1024`, policyTitle: `Standard terms`, policyType } } : {})), (config.targetResource === ActionTargetResource.INVOICE ? { invoice: SAMPLE_INVOICE } : {})), { actionId: `48213`, actionType, actionStatus: status, actionNotes: [], actionDate: SAMPLE_DATE }), (status === ActionStatus.FAILED ? { actionError: `Invoice is in a locked period` } : {})), (actionType === ActionType.SEND_SMS ? { smsPayload: {
            to: `+64211234567`,
            from: `Paidnice`,
            body: `Hi Jane, invoice INV-0042 for $1,250.00 is now overdue. Pay here: https://portal.paidnice.com/i/8d2e4b`,
        } } : {}));
}
//# sourceMappingURL=webhookEvent.js.map